/**
 * FinancialTab - Compact lease/transient financial summary
 *
 * Blocks for Based: Active Contract, Fuel Commitment, Billing placeholder
 * Blocks for Transient: Booking Detail, Billing placeholder
 * No Contract: Create Contract CTA
 */

import { useState } from 'react'
import { toast } from 'sonner'
import { DollarSign, Calendar, FileText, TrendingUp, FilePlus, Repeat, RefreshCw, Pencil, LogOut, Ban } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { LEASE_STATUS_LABELS, LEASE_TYPE_LABELS, ESCALATION_TYPE_LABELS, canTerminate } from '@/types/leases'
import { TRANSIENT_STATUS_LABELS, BOOKING_SOURCE_LABELS, RATE_TYPE_LABELS } from '@/types/transient-stays'
import type { BookingSource, RateType as TransientRateType } from '@/types/transient-stays'
import type { EscalationType, LeaseType, RateType } from '@/types/leases'
import { formatCurrency, formatDate, getDaysUntil } from '@/lib/utils/format-business-data'
import { useAircraftFuelSummary } from '@/hooks/useFuelTransactions'
import { getDaysRemainingInMonth } from '@/types/fuel-transactions'
import { CompactInfoBlock } from './shared/CompactInfoBlock'
import { CommitmentProgress } from './shared/CommitmentProgress'
import { TooltipValue, CompactRow } from './shared/TooltipValue'
import { PricingSourceBadge } from '@/components/settings/location-pricing/PricingSourceBadge'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import { useAppContext } from '@/hooks/routing/useAppContext'
import { useFinalDepartureWithLeaseComplete } from '@/hooks/useAllocationEvents'
import { useTerminateLease } from '@/hooks/useLeases'
import { LeaseMutateDrawer } from '@/components/leases/LeaseMutateDrawer'
import { TerminateLeaseDialog } from '@/components/leases/TerminateLeaseDialog'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface FinancialTabProps {
  data: AllocationEnrichedData
}

const LEASE_STATUS_BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  draft: 'secondary',
  pending_signature: 'secondary',
  active: 'default',
  expiring: 'outline',
  expired: 'destructive',
  terminated: 'destructive',
  completed: 'outline',
  cancelled: 'secondary',
}

const TRANSIENT_STATUS_BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  reserved: 'secondary',
  confirmed: 'default',
  checked_in: 'default',
  checked_out: 'outline',
  cancelled: 'destructive',
  no_show: 'destructive',
}

function LeaseFinancials({ data }: FinancialTabProps) {
  const lease = data.leaseContract!
  const closeCard = useAircraftCardStore((s) => s.closeCard)
  const { locationId } = useAppContext()
  const { data: fuelSummary, isLoading: fuelLoading } = useAircraftFuelSummary(data.aircraft.id, lease.id)
  const finalDeparture = useFinalDepartureWithLeaseComplete()
  const daysRemaining = getDaysRemainingInMonth()
  const daysUntilEnd = lease.endDate ? getDaysUntil(lease.endDate) : null
  const expiryVariant: 'default' | 'warning' | 'error' =
    daysUntilEnd !== null && daysUntilEnd < 0
      ? 'error'
      : daysUntilEnd !== null && daysUntilEnd <= 30
        ? 'warning'
        : 'default'

  // Lease has issues: expired, expiring within 30d, or non-active status
  const hasLeaseIssue =
    (daysUntilEnd !== null && daysUntilEnd <= 30) ||
    lease.status !== 'active'

  // Drawer states
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [renewalDrawerOpen, setRenewalDrawerOpen] = useState(false)
  const [finalDepartureConfirmOpen, setFinalDepartureConfirmOpen] = useState(false)
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false)
  const terminateMutation = useTerminateLease()

  const confirmFinalDeparture = () => {
    if (!locationId) return
    finalDeparture.mutate(
      {
        allocation_id: data.allocationId,
        aircraft_id: data.aircraft.id,
        location_id: locationId,
        lease_id: lease.id,
        notes: 'Final departure — lease completed via financial tab',
      },
      {
        onSuccess: () => {
          setFinalDepartureConfirmOpen(false)
          toast.success(`${data.aircraft.tailNumber} departed — lease completed`)
          closeCard()
        },
        onError: (err) => {
          toast.error(`Final departure failed: ${err.message}`)
        },
      }
    )
  }

  // Calculate remaining term
  const termDisplay = lease.startDate && lease.endDate
    ? `${formatDate(lease.startDate)} → ${formatDate(lease.endDate)}`
    : lease.startDate
      ? `From ${formatDate(lease.startDate)}`
      : null

  const remainingDisplay = daysUntilEnd !== null && daysUntilEnd >= 0
    ? daysUntilEnd > 30
      ? `${Math.round(daysUntilEnd / 30)} months remaining`
      : `${daysUntilEnd} days remaining`
    : null

  return (
    <div className="space-y-0">
      {/* Block 1: Active Contract */}
      <CompactInfoBlock title="Active Lease" formId="lease-contract">
        {/* Rate display */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            {formatCurrency(lease.monthlyRate)}/month
          </span>
          {locationId && (
            <PricingSourceBadge
              locationId={locationId}
              catalogItemCode="hangar_nightly"
              aircraftId={data.aircraft.id}
            />
          )}
        </div>

        {/* Contract number + type */}
        <CompactRow className="mt-1">
          <TooltipValue
            label="Contract Number"
            value={lease.contractNumber}
            icon={<FileText className="w-3 h-3" />}
          />
          {lease.type && (
            <TooltipValue
              label="Contract Type"
              value={LEASE_TYPE_LABELS[lease.type as LeaseType] || lease.type}
              asBadge
            />
          )}
        </CompactRow>

        {/* Term dates */}
        {termDisplay && (
          <CompactRow className="mt-1">
            <TooltipValue
              label="Term"
              value={termDisplay}
              icon={<Calendar className="w-3 h-3" />}
              variant={expiryVariant}
            />
          </CompactRow>
        )}

        {/* Remaining time */}
        {remainingDisplay && (
          <div className="text-[10px] text-muted-foreground mt-0.5 ml-4">
            {remainingDisplay}
          </div>
        )}

        {/* Rate basis */}
        {lease.rateBasis && (
          <CompactRow className="mt-1">
            <TooltipValue
              label="Rate Basis"
              value={RATE_TYPE_LABELS[lease.rateBasis as RateType] || lease.rateBasis}
              icon={<DollarSign className="w-3 h-3" />}
            />
            {lease.securityDeposit && (
              <TooltipValue
                label="Security Deposit"
                value={formatCurrency(lease.securityDeposit)}
              />
            )}
          </CompactRow>
        )}

        {/* Escalation */}
        {lease.escalationType && lease.escalationType !== 'none' && (
          <CompactRow className="mt-1">
            <TooltipValue
              label="Escalation"
              value={`${ESCALATION_TYPE_LABELS[lease.escalationType as EscalationType]} ${lease.escalationPct ? `(${lease.escalationPct}%)` : ''}`}
              icon={<TrendingUp className="w-3 h-3" />}
            />
          </CompactRow>
        )}

        {/* Status badges */}
        <CompactRow className="mt-1.5">
          <Badge
            variant={LEASE_STATUS_BADGE_VARIANTS[lease.status] || 'outline'}
            className="text-[10px] px-1.5 py-0 h-5"
          >
            {LEASE_STATUS_LABELS[lease.status]}
          </Badge>
          {lease.autoRenew && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
              <Repeat className="w-2.5 h-2.5 mr-0.5" />
              Auto-renew
            </Badge>
          )}
        </CompactRow>

        {/* Lease action buttons — always show Edit, show Renew + Final Departure when issues */}
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border/30">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={() => setEditDrawerOpen(true)}
          >
            <Pencil className="w-3 h-3 mr-1" />Edit
          </Button>
          {canTerminate(lease.status) && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs text-destructive hover:text-destructive"
              onClick={() => setTerminateDialogOpen(true)}
            >
              <Ban className="w-3 h-3 mr-1" />Terminate
            </Button>
          )}
          {hasLeaseIssue && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={() => setRenewalDrawerOpen(true)}
              >
                <RefreshCw className="w-3 h-3 mr-1" />Renew
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2.5 text-xs text-destructive hover:text-destructive"
                onClick={() => setFinalDepartureConfirmOpen(true)}
              >
                <LogOut className="w-3 h-3 mr-1" />Final Departure
              </Button>
            </>
          )}
        </div>
      </CompactInfoBlock>

      {/* Divider */}
      <div className="border-t border-border/50" />

      {/* Block 2: Fuel Commitment with progress bar */}
      {lease.fuelCommitmentGallonsMonthly ? (
        fuelLoading ? (
          <CompactInfoBlock title="Fuel Commitment">
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-3 w-24 mt-2" />
          </CompactInfoBlock>
        ) : (
          <CommitmentProgress
            committedGallons={lease.fuelCommitmentGallonsMonthly}
            deliveredGallons={fuelSummary?.monthlyGallons ?? 0}
            daysRemaining={daysRemaining}
            pricePerGallon={lease.fuelPricePerGallon}
            priceType={lease.fuelPriceType}
          />
        )
      ) : (
        <CompactInfoBlock title="Fuel Commitment">
          <Badge variant="outline" className="text-[10px]">
            No fuel commitment
          </Badge>
        </CompactInfoBlock>
      )}

      {/* Divider */}
      <div className="border-t border-border/50" />

      {/* Block 3: Billing placeholder */}
      <CompactInfoBlock title="Billing">
        <p className="text-xs text-muted-foreground/50 text-center py-2">
          Billing system coming soon
        </p>
      </CompactInfoBlock>

      {/* Edit Drawer */}
      <LeaseMutateDrawer
        open={editDrawerOpen}
        onOpenChange={setEditDrawerOpen}
        leaseId={lease.id}
        lockedLocationId={locationId ?? undefined}
      />

      {/* Renewal Drawer */}
      <LeaseMutateDrawer
        open={renewalDrawerOpen}
        onOpenChange={setRenewalDrawerOpen}
        renewalLeaseId={lease.id}
        lockedLocationId={locationId ?? undefined}
      />

      {/* Final Departure Confirmation */}
      <AlertDialog
        open={finalDepartureConfirmOpen}
        onOpenChange={setFinalDepartureConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Final Departure — {data.aircraft.tailNumber}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will mark {data.aircraft.tailNumber} as permanently departed
              and complete the lease contract
              {lease.contractNumber
                ? ` (${lease.contractNumber})`
                : ''}.
              The aircraft will be removed from the ramp view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmFinalDeparture}
              disabled={finalDeparture.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {finalDeparture.isPending
                ? 'Processing...'
                : 'Confirm Final Departure'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Terminate Lease Dialog */}
      <TerminateLeaseDialog
        open={terminateDialogOpen}
        onOpenChange={setTerminateDialogOpen}
        contractNumber={lease.contractNumber}
        onConfirm={(params) => {
          terminateMutation.mutate(
            { id: lease.id, ...params },
            {
              onSuccess: () => {
                setTerminateDialogOpen(false)
                toast.success(`Lease ${lease.contractNumber} terminated`)
                closeCard()
              },
            }
          )
        }}
        isPending={terminateMutation.isPending}
      />
    </div>
  )
}

function TransientFinancials({ data }: FinancialTabProps) {
  const { locationId } = useAppContext()
  const stay = data.transientStay!

  // Calculate running total
  const nightsDisplay = stay.nightsCount
    ? `${stay.nightsCount} night${stay.nightsCount > 1 ? 's' : ''}`
    : null

  return (
    <div className="space-y-0">
      {/* Block 1: Transient Stay */}
      <CompactInfoBlock title="Transient Booking" formId="transient-stay">
        {/* Rate display */}
        {stay.rateAmount && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              {formatCurrency(stay.rateAmount)}
              {stay.rateType ? `/${RATE_TYPE_LABELS[stay.rateType as TransientRateType]?.replace('Per ', '').toLowerCase() || 'night'}` : '/night'}
            </span>
            {locationId && (
              <PricingSourceBadge
                locationId={locationId}
                catalogItemCode="parking_overnight"
                aircraftId={data.aircraft.id}
              />
            )}
          </div>
        )}

        {/* Booking number */}
        <CompactRow className="mt-1">
          <TooltipValue
            label="Booking Number"
            value={stay.bookingNumber}
            icon={<FileText className="w-3 h-3" />}
          />
        </CompactRow>

        {/* Arrival / Departure */}
        <CompactRow className="mt-1">
          <TooltipValue
            label="Arrival"
            value={formatDate(stay.checkIn)}
            icon={<Calendar className="w-3 h-3" />}
          />
          <span className="text-muted-foreground text-xs">→</span>
          <TooltipValue
            label={stay.actualCheckOut ? 'Actual Departure' : 'Expected Departure'}
            value={formatDate(stay.actualCheckOut || stay.expectedCheckOut)}
          />
        </CompactRow>

        {/* Total + nights */}
        <CompactRow className="mt-1">
          {stay.totalAmount && (
            <TooltipValue
              label="Running Total"
              value={formatCurrency(stay.totalAmount)}
              icon={<DollarSign className="w-3 h-3" />}
            />
          )}
          {nightsDisplay && (
            <TooltipValue
              label="Duration"
              value={nightsDisplay}
            />
          )}
        </CompactRow>

        {/* Source + special requests */}
        <CompactRow className="mt-1">
          {stay.source && (
            <TooltipValue
              label="Booking Source"
              value={BOOKING_SOURCE_LABELS[stay.source as BookingSource] || stay.source}
              asBadge
            />
          )}
        </CompactRow>

        {stay.specialRequests && (
          <div className="mt-1.5 text-[10px] text-muted-foreground bg-muted/50 rounded px-2 py-1 italic">
            {stay.specialRequests}
          </div>
        )}

        {/* Status badge */}
        <CompactRow className="mt-1.5">
          <Badge
            variant={TRANSIENT_STATUS_BADGE_VARIANTS[stay.status] || 'outline'}
            className="text-[10px] px-1.5 py-0 h-5"
          >
            {TRANSIENT_STATUS_LABELS[stay.status]}
          </Badge>
        </CompactRow>
      </CompactInfoBlock>

      {/* Divider */}
      <div className="border-t border-border/50" />

      {/* Block 2: Billing placeholder */}
      <CompactInfoBlock title="Billing">
        <p className="text-xs text-muted-foreground/50 text-center py-2">
          Billing system coming soon
        </p>
      </CompactInfoBlock>
    </div>
  )
}

export function FinancialTab({ data }: FinancialTabProps) {
  const openEditForm = useAircraftCardStore((s) => s.openEditForm)

  if (data.leaseContract) {
    return <LeaseFinancials data={data} />
  }

  if (data.transientStay) {
    return <TransientFinancials data={data} />
  }

  // No contract — Create Contract CTA
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center min-h-[120px]">
      <FilePlus className="w-8 h-8 text-muted-foreground/30 mb-2" />
      <p className="text-sm text-muted-foreground">
        No contract linked
      </p>
      <p className="text-xs text-muted-foreground/60 mt-1 mb-3">
        Create a lease or transient booking
      </p>
      <Button
        variant="default"
        size="sm"
        className="h-8 px-4 text-xs"
        onClick={() => openEditForm('create-contract')}
      >
        <FilePlus className="w-3.5 h-3.5 mr-1.5" />
        Create Contract
      </Button>
    </div>
  )
}
